// ECharts options for the chart recipes the org brief server sends. The
// server builds every point; this file only draws them.
const BLUE = "#39a8f0";
const LIGHT_BLUE = "#a9d5f5";
const PALE_BLUE = "#cde6f8";
const PALEST_BLUE = "#e6f1fa";
const INK = "#1c1e2b";
const MUTED = "#8b8e9e";
const GRID_LINE = "#eef0f4";
const RING_COLORS = ["#a9d5f5", "#e8cc91", "#a5d2b8", "#e5b0b0", "#c0afe3", "#d5d8e0"];

const textStyle = { fontFamily: "inherit", color: MUTED, fontSize: 12 };

const tooltip = (formatter) => ({
  trigger: "item",
  confine: true,
  renderMode: "richText",
  backgroundColor: "#fff",
  borderColor: "#e8e9ee",
  textStyle: { color: INK, fontSize: 12 },
  formatter,
});

const categoryAxis = (labels, highlight) => ({
  type: "category",
  data: labels,
  axisTick: { show: false },
  axisLine: { lineStyle: { color: GRID_LINE } },
  axisLabel: {
    ...textStyle,
    // Show every bucket. ECharts hides labels it thinks will collide.
    interval: 0,
    formatter: (label) => (highlight.includes(label) ? `{strong|${label}}` : `{plain|${label}}`),
    rich: {
      plain: { color: MUTED, fontSize: 12 },
      strong: { color: INK, fontWeight: 600, fontSize: 12 },
    },
  },
});

const valueAxis = (extra = {}) => ({
  type: "value",
  splitLine: { lineStyle: { color: GRID_LINE } },
  axisLabel: textStyle,
  ...extra,
});

const grid = { left: 4, right: 4, top: 12, bottom: 4, containLabel: true };

function barOption(chart) {
  const labels = chart.points.map((point) => point.label);
  const highlight = chart.highlight || [];
  const hasRatings = chart.points.some((point) => point.rating !== null && point.rating !== undefined);
  return {
    animationDuration: 400,
    grid: hasRatings ? { ...grid, right: 8 } : grid,
    tooltip: tooltip((params) => {
      const point = chart.points[params.dataIndex];
      if (params.seriesName === "Rating") {
        return `${point.label}\nRating ${point.rating} from ${point.ratingCount} ratings`;
      }
      return `${point.label}\n${point.display}`;
    }),
    xAxis: categoryAxis(labels, highlight),
    yAxis: hasRatings
      ? [valueAxis(), valueAxis({ min: 0, max: 5, interval: 1, splitLine: { show: false } })]
      : valueAxis(chart.unit === "stars" ? { min: 0, max: 5, interval: 1 } : {}),
    series: [
      {
        name: "Value",
        type: "bar",
        barMaxWidth: 34,
        data: chart.points.map((point) => ({
          value: point.value,
          itemStyle: {
            color: highlight.length === 0 || highlight.includes(point.label) ? BLUE : LIGHT_BLUE,
            borderRadius: [5, 5, 0, 0],
          },
        })),
      },
      ...(hasRatings
        ? [
            {
              name: "Rating",
              type: "scatter",
              yAxisIndex: 1,
              symbolSize: 9,
              itemStyle: { color: "#fff", borderColor: INK, borderWidth: 2 },
              data: chart.points.map((point) => point.rating ?? null),
            },
          ]
        : []),
    ],
  };
}

function stackedBarOption(chart) {
  const labels = chart.points.map((point) => point.label);
  const last = labels.length - 1;
  const [primary, secondary] = chart.series;
  const colored = (values, current, past, radius) =>
    values.map((value, index) => ({
      value,
      itemStyle: { color: index === last ? current : past, borderRadius: radius },
    }));
  return {
    animationDuration: 400,
    grid,
    tooltip: tooltip(
      (params) =>
        `${labels[params.dataIndex]}\n${primary.name} ${primary.values[params.dataIndex]}h\n${secondary.name} ${secondary.values[params.dataIndex]}h`,
    ),
    xAxis: categoryAxis(labels, chart.highlight || []),
    yAxis: valueAxis(),
    series: [
      {
        name: primary.name,
        type: "bar",
        stack: "time",
        barMaxWidth: 34,
        data: colored(primary.values, BLUE, LIGHT_BLUE, 0),
      },
      {
        name: secondary.name,
        type: "bar",
        stack: "time",
        barMaxWidth: 34,
        data: colored(secondary.values, PALE_BLUE, PALEST_BLUE, [5, 5, 0, 0]),
      },
    ],
  };
}

function lineOption(chart) {
  const labels = chart.points.map((point) => point.label);
  const last = chart.points.length - 1;
  return {
    animationDuration: 400,
    grid,
    tooltip: tooltip((params) => `${chart.points[params.dataIndex].label}\n${chart.points[params.dataIndex].display}`),
    xAxis: { ...categoryAxis(labels, chart.highlight || []), boundaryGap: false },
    yAxis: valueAxis({ scale: true }),
    series: [
      {
        type: "line",
        smooth: true,
        symbol: "circle",
        symbolSize: (value, params) => (params.dataIndex === last ? 9 : 5),
        lineStyle: { color: BLUE, width: 2.5 },
        itemStyle: { color: BLUE },
        areaStyle: { color: "rgba(57, 168, 240, 0.08)" },
        data: chart.points.map((point) => point.value),
      },
    ],
  };
}

// Ring slices and the HTML legend beside the ring share these colors.
export function ringSliceColor(chart, index) {
  return (chart.highlight || []).includes(chart.points[index].label)
    ? BLUE
    : RING_COLORS[index % RING_COLORS.length];
}

// The legend and the center total are HTML in OrgBriefChartCard. Long labels
// such as goal names do not fit inside the canvas.
function ringOption(chart) {
  return {
    animationDuration: 400,
    tooltip: tooltip((params) => `${params.name}\n${chart.points[params.dataIndex].display} · ${params.percent}%`),
    series: [
      {
        type: "pie",
        radius: ["62%", "86%"],
        center: ["50%", "50%"],
        label: { show: false },
        labelLine: { show: false },
        itemStyle: { borderWidth: 2, borderColor: "#fff", borderRadius: 3 },
        data: chart.points.map((point, index) => ({
          name: point.label,
          value: point.value,
          itemStyle: { color: ringSliceColor(chart, index) },
        })),
      },
    ],
  };
}

function hbarOption(chart) {
  const points = [...chart.points].reverse();
  const highlight = chart.highlight || [];
  return {
    animationDuration: 400,
    grid: { ...grid, right: 40 },
    tooltip: tooltip((params) => `${points[params.dataIndex].label}\n${points[params.dataIndex].display}`),
    xAxis: valueAxis({ splitLine: { show: false }, axisLabel: { show: false } }),
    yAxis: {
      type: "category",
      data: points.map((point) => point.label),
      axisTick: { show: false },
      axisLine: { show: false },
      axisLabel: { ...textStyle, color: "#43465a", width: 120, overflow: "truncate" },
    },
    series: [
      {
        type: "bar",
        barMaxWidth: 16,
        label: {
          show: true,
          position: "right",
          ...textStyle,
          formatter: (params) => points[params.dataIndex].display,
        },
        data: points.map((point) => ({
          value: point.value,
          itemStyle: {
            color: highlight.length === 0 || highlight.includes(point.label) ? BLUE : LIGHT_BLUE,
            borderRadius: [0, 4, 4, 0],
          },
        })),
      },
    ],
  };
}

const BUILDERS = {
  bar: barOption,
  stacked_bar: stackedBarOption,
  line: lineOption,
  ring: ringOption,
  hbar: hbarOption,
};

export default function buildOrgBriefChartOption(chart) {
  const builder = BUILDERS[chart?.kind];
  return builder && chart.points?.length ? builder(chart) : null;
}
